import { useState, useMemo } from "react";

import { Checkbox } from "primereact/checkbox";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { Chip } from "primereact/chip";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";

import {
  LESSONS_RELEASE_OPTIONS,
  getLanguageDisplayName,
  getTechnologyDisplayName,
} from "sj-ui-toolkit/src/constants/content";
import { getSelectedTagLabel } from "sj-ui-toolkit/src/utils/catalogTags";

import type { CatalogTagOption } from "sj-ui-toolkit/src/client/svcContent";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LessonFiltersState {
  focus: string[];
  difficulty: string[];
  release: string[];
  contentType: string[];
  languages: string[];
  technologies: string[];
  cwe: number[];
}

export interface MappedCweTag {
  value: number;
  name: string;
  description: string | null;
}

interface LessonFilterPanelProps {
  isOpen: boolean;
  onToggle: () => void;
  tagsData: Record<string, CatalogTagOption[]>;
  cweTags: MappedCweTag[];
  filters: LessonFiltersState;
  onFilterChange: (category: string, values: string[] | number[]) => void;
  onClearFilters: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const LessonFilterPanel = ({
  isOpen,
  onToggle,
  tagsData,
  cweTags,
  filters,
  onFilterChange,
  onClearFilters,
}: LessonFilterPanelProps) => {
  const [languageSearch, setLanguageSearch] = useState("");
  const [technologySearch, setTechnologySearch] = useState("");
  const [cweSearch, setCweSearch] = useState("");

  const difficultyOptions = tagsData.difficulty ?? [];
  const contentTypeOptions = tagsData.contentType ?? [];

  const tags = useMemo(() => {
    const languages = (tagsData.languages ?? [])
      .map(t => t.id)
      .filter((id): id is string => id !== undefined);
    const technologies = (tagsData.technologies ?? [])
      .map(t => t.id)
      .filter((id): id is string => id !== undefined);
    return { languages: [...languages].sort(), technologies: [...technologies].sort() };
  }, [tagsData]);

  const filteredLanguages = useMemo(() => {
    if (!languageSearch) return tags.languages;
    const s = languageSearch.toLowerCase();
    return tags.languages.filter(l => l.toLowerCase().includes(s));
  }, [tags.languages, languageSearch]);

  const filteredTechnologies = useMemo(() => {
    if (!technologySearch) return tags.technologies;
    const s = technologySearch.toLowerCase();
    return tags.technologies.filter(t => t.toLowerCase().includes(s));
  }, [tags.technologies, technologySearch]);

  const filteredCwe = useMemo(() => {
    if (!cweTags.length) return [];
    if (!cweSearch) return cweTags;
    const s = cweSearch.toLowerCase();
    return cweTags.filter(
      cwe =>
        cwe.value.toString().includes(s) ||
        cwe.name.toLowerCase().includes(s) ||
        (cwe.description && cwe.description.toLowerCase().includes(s))
    );
  }, [cweTags, cweSearch]);

  const hasDynamicFilters =
    tags.languages.length > 1 ||
    tags.technologies.length > 1 ||
    cweTags.length > 1 ||
    contentTypeOptions.length > 1 ||
    difficultyOptions.length > 1;

  if (!hasDynamicFilters) return null;

  const handleCheckboxChange = (category: string, value: string | number) => {
    const current = (filters[category as keyof LessonFiltersState] as (string | number)[]) ?? [];
    const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
    onFilterChange(category, next as string[] | number[]);
  };

  const isChecked = (category: string, value: string | number): boolean =>
    ((filters[category as keyof LessonFiltersState] as (string | number)[]) ?? []).includes(value);

  // Active filter chips
  interface FilterChip {
    key: string;
    label: string;
    category: string;
    value?: string | number;
    isGroup?: boolean;
  }

  const getActiveFiltersInfo = () => {
    const activeFilters: FilterChip[] = [];
    let totalCount = 0;

    (["difficulty", "release", "contentType"] as const).forEach(category => {
      const values = (filters[category] as string[]) ?? [];
      values.forEach(value => {
        totalCount++;
        if (activeFilters.length < 5) {
          let label = value;
          if (category === "difficulty") {
            label = getSelectedTagLabel(difficultyOptions, value);
          } else if (category === "contentType") {
            label = contentTypeOptions.find(o => o.id === value)?.displayName ?? value;
          } else if (category === "release") {
            label = LESSONS_RELEASE_OPTIONS.find(o => o.value === value)?.label ?? value;
          }
          activeFilters.push({ key: `${category}-${value}`, label, category, value });
        }
      });
    });

    const selectedLanguages = filters.languages ?? [];
    if (selectedLanguages.length > 0) {
      totalCount++;
      if (activeFilters.length < 5) {
        activeFilters.push(
          selectedLanguages.length === 1
            ? {
                key: `languages-${selectedLanguages[0]}`,
                label: getLanguageDisplayName(selectedLanguages[0] ?? ""),
                category: "languages",
                value: selectedLanguages[0],
              }
            : {
                key: "languages-group",
                label: `${selectedLanguages.length} Languages`,
                category: "languages",
                isGroup: true,
              }
        );
      }
    }

    const selectedTechnologies = filters.technologies ?? [];
    if (selectedTechnologies.length > 0) {
      totalCount++;
      if (activeFilters.length < 5) {
        activeFilters.push(
          selectedTechnologies.length === 1
            ? {
                key: `technologies-${selectedTechnologies[0]}`,
                label: getTechnologyDisplayName(selectedTechnologies[0] ?? ""),
                category: "technologies",
                value: selectedTechnologies[0],
              }
            : {
                key: "technologies-group",
                label: `${selectedTechnologies.length} Technologies`,
                category: "technologies",
                isGroup: true,
              }
        );
      }
    }

    const selectedCwe = filters.cwe ?? [];
    if (selectedCwe.length > 0) {
      totalCount++;
      if (activeFilters.length < 5) {
        if (selectedCwe.length === 1) {
          const cweData = cweTags.find(cwe => cwe.value === selectedCwe[0]);
          activeFilters.push({
            key: `cwe-${selectedCwe[0]}`,
            label: cweData?.name ?? `CWE-${selectedCwe[0]}`,
            category: "cwe",
            value: selectedCwe[0],
          });
        } else {
          activeFilters.push({
            key: "cwe-group",
            label: `${selectedCwe.length} CWEs`,
            category: "cwe",
            isGroup: true,
          });
        }
      }
    }

    return { activeFilters, totalCount, remainingCount: totalCount - activeFilters.length };
  };

  const { activeFilters, totalCount, remainingCount } = getActiveFiltersInfo();

  const handleRemoveFilter = (filter: FilterChip) => {
    if (filter.isGroup) {
      onFilterChange(filter.category, []);
    } else if (filter.value !== undefined) {
      handleCheckboxChange(filter.category, filter.value);
    }
  };

  return (
    <div className="mt-2 ml-4 mr-4">
      {/* Header row */}
      <div className="flex items-center justify-between gap-6 flex-wrap">
        <Button
          label="Advanced Filters"
          text
          onClick={onToggle}
          icon={`pi ${isOpen ? "pi-chevron-up" : "pi-chevron-down"}`}
          className="text-color font-bold"
        />
        {totalCount > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            {activeFilters.map(filter => (
              <Chip
                key={filter.key}
                label={filter.label}
                removable
                onRemove={() => {
                  handleRemoveFilter(filter);
                  return false;
                }}
                className="bg-surface-750 text-surface-0 body-xs font-medium px-3 py-1 rounded-full"
              />
            ))}
            {remainingCount > 0 && (
              <span className="body-xs text-secondary font-medium">+{remainingCount} more</span>
            )}
            <Button label="Clear filters" text size="small" onClick={onClearFilters} />
          </div>
        )}
      </div>

      {/* Collapsible panel */}
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <div className="mt-6 pt-6 border-t border-surface-300">
            <div
              className="grid gap-8"
              style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}
            >
              {/* Language */}
              {tags.languages.length > 1 && (
                <div className="min-w-0">
                  <h4 className="text-[0.6875rem] font-bold uppercase tracking-[0.08em] text-secondary mb-3">
                    Language
                  </h4>
                  <div className="mb-3">
                    <IconField iconPosition="left">
                      <InputIcon className="pi pi-search" />
                      <InputText
                        value={languageSearch}
                        onChange={e => setLanguageSearch(e.target.value)}
                        placeholder="Search languages"
                        className="w-[90%] body-sm"
                      />
                    </IconField>
                  </div>
                  <div className="flex flex-col gap-2 max-h-[280px] overflow-y-auto atlas-scrollbar">
                    {filteredLanguages.map(lang => (
                      <div key={lang} className="flex items-center gap-2">
                        <Checkbox
                          inputId={`language-${lang}`}
                          checked={isChecked("languages", lang)}
                          onChange={() => handleCheckboxChange("languages", lang)}
                        />
                        <label htmlFor={`language-${lang}`} className="body-sm cursor-pointer">
                          {getLanguageDisplayName(lang)}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Technology */}
              {tags.technologies.length > 1 && (
                <div className="min-w-0">
                  <h4 className="text-[0.6875rem] font-bold uppercase tracking-[0.08em] text-secondary mb-3">
                    Technology
                  </h4>
                  <div className="mb-3">
                    <IconField iconPosition="left">
                      <InputIcon className="pi pi-search" />
                      <InputText
                        value={technologySearch}
                        onChange={e => setTechnologySearch(e.target.value)}
                        placeholder="Search technologies"
                        className="w-[90%] body-sm"
                      />
                    </IconField>
                  </div>
                  <div className="flex flex-col gap-2 max-h-[280px] overflow-y-auto atlas-scrollbar">
                    {filteredTechnologies.map(tech => (
                      <div key={tech} className="flex items-center gap-2">
                        <Checkbox
                          inputId={`technology-${tech}`}
                          checked={isChecked("technologies", tech)}
                          onChange={() => handleCheckboxChange("technologies", tech)}
                        />
                        <label htmlFor={`technology-${tech}`} className="body-sm cursor-pointer">
                          {getTechnologyDisplayName(tech)}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* CWE */}
              {cweTags.length > 1 && (
                <div className="min-w-0">
                  <h4 className="text-[0.6875rem] font-bold uppercase tracking-[0.08em] text-secondary mb-3">
                    Common Weakness Enumerations (CWE)
                  </h4>
                  <div className="mb-3">
                    <IconField iconPosition="left">
                      <InputIcon className="pi pi-search" />
                      <InputText
                        value={cweSearch}
                        onChange={e => setCweSearch(e.target.value)}
                        placeholder="Search by CWE number or name"
                        className="w-[90%] body-sm"
                      />
                    </IconField>
                  </div>
                  <div className="flex flex-col gap-2 max-h-[280px] overflow-y-auto atlas-scrollbar">
                    {filteredCwe.map(cwe => (
                      <div key={cwe.value} className="flex items-center gap-2">
                        <Checkbox
                          inputId={`cwe-${cwe.value}`}
                          checked={isChecked("cwe", cwe.value)}
                          onChange={() => handleCheckboxChange("cwe", cwe.value)}
                        />
                        <label
                          htmlFor={`cwe-${cwe.value}`}
                          className="body-sm cursor-pointer cwe-tooltip-target"
                          data-pr-tooltip={
                            cwe.description ? `${cwe.name}\n\n${cwe.description}` : cwe.name
                          }
                        >
                          {cwe.name}
                        </label>
                      </div>
                    ))}
                  </div>
                  <Tooltip target=".cwe-tooltip-target" position="bottom" className="max-w-md" />
                </div>
              )}

              {/* Content Type */}
              {contentTypeOptions.length > 1 && (
                <div className="min-w-0">
                  <h4 className="text-[0.6875rem] font-bold uppercase tracking-[0.08em] text-secondary mb-3">
                    Content Type
                  </h4>
                  <div className="flex flex-col gap-2">
                    {contentTypeOptions.map(option => (
                      <div key={option.id} className="flex items-center gap-2">
                        <Checkbox
                          inputId={`contentType-${option.id}`}
                          checked={isChecked("contentType", option.id ?? "")}
                          onChange={() => handleCheckboxChange("contentType", option.id ?? "")}
                        />
                        <label
                          htmlFor={`contentType-${option.id}`}
                          className="body-sm cursor-pointer"
                        >
                          {option.displayName}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Difficulty */}
              {difficultyOptions.length > 1 && (
                <div className="min-w-0">
                  <h4 className="text-[0.6875rem] font-bold uppercase tracking-[0.08em] text-secondary mb-3">
                    Difficulty
                  </h4>
                  <div className="flex flex-col gap-2">
                    {difficultyOptions.map(option => (
                      <div key={option.id} className="flex items-center gap-2">
                        <Checkbox
                          inputId={`difficulty-${option.id}`}
                          checked={isChecked("difficulty", option.id ?? "")}
                          onChange={() => handleCheckboxChange("difficulty", option.id ?? "")}
                        />
                        <label
                          htmlFor={`difficulty-${option.id}`}
                          className="body-sm cursor-pointer"
                        >
                          {option.displayName}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Release */}
              <div className="min-w-0">
                <h4 className="text-[0.6875rem] font-bold uppercase tracking-[0.08em] text-secondary mb-3">
                  Release
                </h4>
                <div className="flex flex-col gap-2">
                  {LESSONS_RELEASE_OPTIONS.map(option => (
                    <div key={option.value} className="flex items-center gap-2">
                      <Checkbox
                        inputId={`release-${option.value}`}
                        checked={isChecked("release", option.value)}
                        onChange={() => handleCheckboxChange("release", option.value)}
                      />
                      <label htmlFor={`release-${option.value}`} className="body-sm cursor-pointer">
                        {option.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LessonFilterPanel;
